import React, { useEffect, useMemo, useState } from 'react';
import { CheckPicker, DateRangePicker, Message, Placeholder, Toggle } from 'rsuite';
import { FilterField, FilterToolbar, renderSelectAllFooter } from '@nsi-advisory-services/components';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import ExpenseList from '../components/expenses/ExpenseList';
import ExpenseGroupModal from '../components/expenses/ExpenseGroupModal';
import ExpenseMarkPaidModal from '../components/expenses/ExpenseMarkPaidModal';
import { buildExpenseGroups, flattenExpenseGroups } from '../utils/expenseGroups';
import TimeOffList from '../components/timeoff/TimeOffList';
import PageHeader from '../components/shared/PageHeader';
import { toDateOnlyString, toStartOfDay } from '../utils/payPeriods';
import { getQuickDateRanges } from '../utils/dateRanges';
import ApprovalReviewModal from '../components/timeoff/ApprovalReviewModal';
import {
  useApproveExpense,
  useApproveTimeOffRequest,
  useCommentExpense,
  useCommentTimeOffRequest,
  useExpenses,
  useMarkExpensePaid,
  useRejectExpense,
  useRejectTimeOffRequest,
  useTimeOffRequests,
  useUpdateEmployee,
} from '../hooks/useQueries';
import './ApprovalsPage.css';
import { renderEmployeeSelection } from '../components/shared/employeeSelection';

const VACATION_APPROVAL_NOTIFICATIONS_PREF_KEY = 'vacation_approval_notifications_enabled';
const STALE_APPROVAL_TOAST = 'This request has been modified. The approvals list has been refreshed.';

// The server refuses to approve, reject or comment on a request that is no
// longer pending; any of those means someone else got there first, so match the
// shared prefix rather than each verb.
const isStaleTimeOffDecisionError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('only pending requests can be')
    || message.includes('request not found')
  );
};

const isStaleExpenseDecisionError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  return (
    message.includes('only pending expenses can be')
    || message.includes('expense not found')
  );
};

const isPendingStatus = (value) => String(value || '').toLowerCase() === 'pending';

const isEnteredRequest = (request) => {
  const status = String(request?.status || '').toLowerCase();
  const approvalReason = String(request?.approval_reason || '').trim().toLowerCase();
  return status === 'approved' && approvalReason === 'no approval required';
};

const showApprovalToast = (type, content) => showToast(type, content, { duration: 5000 });

const normalizePreferences = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const readVacationApprovalNotificationPreference = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (['false', '0', 'no', 'off', 'disabled'].includes(normalized)) return false;
  if (['true', '1', 'yes', 'on', 'enabled'].includes(normalized)) return true;
  return true;
};

export default function ApprovalsPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const currentEmployeeId = String(auth.employee?.id || '');
  const canApproveTimeOff = auth.isApprover() || auth.isAdmin() || auth.hasPermission('approve_time_off');
  const canApproveExpenses = auth.isFinance() || auth.isAdmin() || auth.hasPermission('approve_expenses');
  const canManagePayroll = auth.isFinance() || auth.isAdmin() || auth.hasPermission('manage_payroll');
  const canViewPage = canApproveTimeOff || canApproveExpenses;

  const timeOffQuery = useTimeOffRequests({
    enabled: canApproveTimeOff,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
  const expensesQuery = useExpenses({
    enabled: canApproveExpenses,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });
  const approveExpenseMutation = useApproveExpense();
  const rejectExpenseMutation = useRejectExpense();
  const approveTimeOffMutation = useApproveTimeOffRequest();
  const rejectTimeOffMutation = useRejectTimeOffRequest();
  const commentExpenseMutation = useCommentExpense();
  const commentTimeOffMutation = useCommentTimeOffRequest();
  const updateEmployeeMutation = useUpdateEmployee();
  const markExpensePaidMutation = useMarkExpensePaid();

  // Empty means "no restriction" for both, so the pickers read "All …" rather
  // than opening with everything ticked. The queue still starts on Pending,
  // which is what an approver comes here to work.
  const [statusFilter, setStatusFilter] = useState(['pending']);
  const [employeeFilter, setEmployeeFilter] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [activeRecord, setActiveRecord] = useState(null);
  const [activeRecordType, setActiveRecordType] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [openGroupId, setOpenGroupId] = useState(null);
  // The expense the payment-date dialog was opened on, kept apart from
  // activeRecord so the approval details it was opened from stay on screen
  // underneath it.
  const [paidExpense, setPaidExpense] = useState(null);
  const [paidOnDate, setPaidOnDate] = useState(null);
  const [notificationToggleError, setNotificationToggleError] = useState(null);

  const isSelfAdmin = auth.isAdmin();
  const currentPreferences = useMemo(
    () => normalizePreferences(auth.employee?.preferences),
    [auth.employee?.preferences]
  );
  const currentVacationApprovalNotificationSetting = useMemo(
    () => readVacationApprovalNotificationPreference(currentPreferences?.[VACATION_APPROVAL_NOTIFICATIONS_PREF_KEY]),
    [currentPreferences]
  );
  const [vacationApprovalNotificationSubscribed, setVacationApprovalNotificationSubscribed] = useState(
    currentVacationApprovalNotificationSetting
  );

  useEffect(() => {
    setVacationApprovalNotificationSubscribed(currentVacationApprovalNotificationSetting);
  }, [currentVacationApprovalNotificationSetting]);

  // No "All" entry: an empty selection already means all, and leaving both in
  // would be two ways to say the same thing.
  const statusOptions = useMemo(
    () => [
      { label: 'Pending', value: 'pending' },
      { label: 'Approved', value: 'approved' },
      { label: 'Rejected', value: 'rejected' },
    ],
    []
  );

  const quickDateRanges = useMemo(() => getQuickDateRanges(), []);

  // Both queues are filtered on the window they overlap rather than on a single
  // date: a time off request and a multi-month tech reimbursement both span a
  // range, and either counts if any part of it falls inside.
  const [rangeStart, rangeEnd] = useMemo(() => {
    const [rawStart, rawEnd] = Array.isArray(dateRange) ? dateRange : [];
    return [toStartOfDay(rawStart), toStartOfDay(rawEnd)];
  }, [dateRange]);

  const overlapsRange = useMemo(() => (from, to) => {
    if (!rangeStart || !rangeEnd) return true;
    const start = toStartOfDay(from);
    const end = toStartOfDay(to) || start;
    if (!start || !end) return false;
    return start <= rangeEnd && end >= rangeStart;
  }, [rangeStart, rangeEnd]);

  const visibleBaseTimeOff = useMemo(() => {
    const all = timeOffQuery.data || [];
    return all.filter((request) => {
      const isSelf = currentEmployeeId && String(request.employee_id) === currentEmployeeId;
      if (isSelf) return false;
      if (isEnteredRequest(request)) return false;
      return true;
    });
  }, [currentEmployeeId, timeOffQuery.data]);

  const filteredTimeOff = useMemo(() => {
    const statuses = new Set(statusFilter);
    const employees = new Set(employeeFilter.map((id) => String(id)));

    return visibleBaseTimeOff.filter((request) => {
      const matchesStatus = statuses.size === 0 || statuses.has(String(request.status).toLowerCase());
      const matchesEmployee = employees.size === 0 || employees.has(String(request.employee_id));
      const matchesRange = overlapsRange(request.start_date, request.end_date);
      return matchesStatus && matchesEmployee && matchesRange;
    }).sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [employeeFilter, statusFilter, overlapsRange, visibleBaseTimeOff]);

  const statusSummary = useMemo(() => {
    const summary = { pending: 0, approved: 0, rejected: 0 };
    for (const request of visibleBaseTimeOff) {
      const key = String(request?.status || '').toLowerCase();
      if (key === 'pending' || key === 'approved' || key === 'rejected') {
        summary[key] += 1;
      }
    }
    return summary;
  }, [visibleBaseTimeOff]);

  const visibleBaseExpenses = useMemo(() => {
    return expensesQuery.data || [];
  }, [expensesQuery.data]);

  const filteredExpenses = useMemo(() => {
    const statuses = new Set(statusFilter);
    const employees = new Set(employeeFilter.map((id) => String(id)));

    return visibleBaseExpenses.filter((expense) => {
      const matchesStatus = statuses.size === 0 || statuses.has(String(expense.status).toLowerCase());
      const matchesEmployee = employees.size === 0 || employees.has(String(expense.employee_id));
      // Tech reimbursements span many months, so use the purchase range when it
      // is there and fall back to the single purchase date.
      const matchesRange = overlapsRange(
        expense.purchase_start_date || expense.purchase_date,
        expense.purchase_end_date || expense.purchase_date,
      );
      return matchesStatus && matchesEmployee && matchesRange;
    }).sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [employeeFilter, statusFilter, overlapsRange, visibleBaseExpenses]);

  // Expenses submitted as a batch stay in the queue with everything else, but
  // run together under a header row naming the batch, so a twelve receipt trip
  // reads as one thing. Each expense inside is still reviewed and decided
  // individually.
  const expenseView = useMemo(() => buildExpenseGroups(filteredExpenses), [filteredExpenses]);
  const expenseRows = useMemo(() => flattenExpenseGroups(expenseView), [expenseView]);

  // Read from the live list so the card reflects decisions made inside it.
  const openGroup = useMemo(() => {
    if (!openGroupId) return null;
    return expenseView.groups.find((group) => String(group.id) === String(openGroupId)) || null;
  }, [openGroupId, expenseView.groups]);

  const employeeOptions = useMemo(() => {
    const rows = [...visibleBaseTimeOff, ...visibleBaseExpenses];
    const byId = new Map();

    rows.forEach((row) => {
      if (!row?.employee_id) return;
      const id = String(row.employee_id);
      if (byId.has(id)) return;

      const first = row.first_name || row.employee_first_name || '';
      const last = row.last_name || row.employee_last_name || '';
      const email = row.email || row.employee_email || '';
      const name = `${first} ${last}`.trim();
      const label = [name || 'Unknown Employee', email].filter(Boolean).join(' • ');

      byId.set(id, {
        value: id,
        label,
      });
    });

    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleBaseExpenses, visibleBaseTimeOff]);

  const openReviewTimeOff = async (row) => {
    if (currentEmployeeId && String(row?.employee_id) === currentEmployeeId) return;

    let latestRows = timeOffQuery.data || [];
    try {
      const refreshed = await timeOffQuery.refetch();
      latestRows = refreshed?.data || latestRows;
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to refresh approvals'));
      return;
    }

    const latest = latestRows.find((request) => String(request?.id) === String(row?.id));
    if (!latest || isEnteredRequest(latest) || !isPendingStatus(latest.status)) {
      showApprovalToast('warning', STALE_APPROVAL_TOAST);
      return;
    }

    setActiveRecord(latest);
    setActiveRecordType('timeoff');
    setReviewOpen(true);
  };

  const openViewTimeOff = async (row) => {
    if (currentEmployeeId && String(row?.employee_id) === currentEmployeeId) return;

    let latestRows = timeOffQuery.data || [];
    try {
      const refreshed = await timeOffQuery.refetch();
      latestRows = refreshed?.data || latestRows;
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to refresh approvals'));
      return;
    }

    const latest = latestRows.find((request) => String(request?.id) === String(row?.id));
    if (!latest || isEnteredRequest(latest)) {
      showApprovalToast('warning', STALE_APPROVAL_TOAST);
      return;
    }

    setActiveRecord(latest);
    setActiveRecordType('timeoff');
    setReviewOpen(true);
  };

  const openReviewExpense = async (row) => {
    let latestRows = expensesQuery.data || [];
    try {
      const refreshed = await expensesQuery.refetch();
      latestRows = refreshed?.data || latestRows;
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to refresh approvals'));
      return;
    }

    const latest = latestRows.find((expense) => String(expense?.id) === String(row?.id));
    if (!latest || !isPendingStatus(latest.status)) {
      showApprovalToast('warning', STALE_APPROVAL_TOAST);
      return;
    }

    setActiveRecord(latest);
    setActiveRecordType('expense');
    setReviewOpen(true);
  };

  const openViewExpense = async (row) => {
    let latestRows = expensesQuery.data || [];
    try {
      const refreshed = await expensesQuery.refetch();
      latestRows = refreshed?.data || latestRows;
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to refresh approvals'));
      return;
    }

    const latest = latestRows.find((expense) => String(expense?.id) === String(row?.id));
    if (!latest) {
      showApprovalToast('warning', STALE_APPROVAL_TOAST);
      return;
    }

    setActiveRecord(latest);
    setActiveRecordType('expense');
    setReviewOpen(true);
  };

  // Paying a reimbursement is payroll's job rather than an approver's, but
  // whoever does both would otherwise have to leave for the payroll view just
  // to record the date. This opens the same dialog that view opens.
  const openMarkPaid = (expense) => {
    if (!expense?.id) return;
    setPaidExpense(expense);
    setPaidOnDate(new Date());
  };

  const closeMarkPaid = () => {
    setPaidExpense(null);
    setPaidOnDate(null);
  };

  const submitMarkPaid = async () => {
    if (!paidExpense?.id || !paidOnDate) return;
    const paidOn = toDateOnlyString(paidOnDate);

    try {
      await markExpensePaidMutation.mutateAsync({ id: paidExpense.id, paid_on: paidOn });
      // The approval details are still open behind this dialog, so carry the
      // date over rather than leaving them describing an unpaid expense.
      setActiveRecord((prev) => (
        prev && String(prev.id) === String(paidExpense.id) ? { ...prev, paid_on: paidOn } : prev
      ));
      closeMarkPaid();
      showApprovalToast('success', 'Expense marked paid');
    } catch (error) {
      showApprovalToast('error', String(error?.message || error || 'Unable to mark this expense paid'));
      await expensesQuery.refetch();
    }
  };

  const submitApprove = async (approval_reason) => {
    if (!activeRecord?.id) return;
    try {
      if (activeRecordType === 'timeoff') {
        await approveTimeOffMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      } else if (activeRecordType === 'expense') {
        await approveExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      }

      setReviewOpen(false);
      setActiveRecord(null);
      setActiveRecordType(null);
    } catch (error) {
      const stale = activeRecordType === 'expense'
        ? isStaleExpenseDecisionError(error)
        : isStaleTimeOffDecisionError(error);

      if (stale) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        setReviewOpen(false);
        setActiveRecord(null);
        setActiveRecordType(null);
        if (activeRecordType === 'expense') {
          await expensesQuery.refetch();
        } else {
          await timeOffQuery.refetch();
        }
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to approve request'));
    }
  };

  // Saves the comment and leaves the record pending, so it stays in the queue.
  const submitComment = async (approval_reason) => {
    if (!activeRecord?.id) return;
    try {
      if (activeRecordType === 'timeoff') {
        await commentTimeOffMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      } else if (activeRecordType === 'expense') {
        await commentExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      }

      showApprovalToast('success', 'Comment saved');
      setReviewOpen(false);
      setActiveRecord(null);
      setActiveRecordType(null);
    } catch (error) {
      const stale = activeRecordType === 'expense'
        ? isStaleExpenseDecisionError(error)
        : isStaleTimeOffDecisionError(error);

      if (stale) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        setReviewOpen(false);
        setActiveRecord(null);
        setActiveRecordType(null);
        if (activeRecordType === 'expense') {
          await expensesQuery.refetch();
        } else {
          await timeOffQuery.refetch();
        }
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to save comment'));
    }
  };

  const submitReject = async (approval_reason) => {
    if (!activeRecord?.id) return;
    try {
      if (activeRecordType === 'timeoff') {
        await rejectTimeOffMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      } else if (activeRecordType === 'expense') {
        await rejectExpenseMutation.mutateAsync({ id: activeRecord.id, approval_reason });
      }
      setReviewOpen(false);
      setActiveRecord(null);
      setActiveRecordType(null);
    } catch (error) {
      const stale = activeRecordType === 'expense'
        ? isStaleExpenseDecisionError(error)
        : isStaleTimeOffDecisionError(error);

      if (stale) {
        showApprovalToast('warning', STALE_APPROVAL_TOAST);
        setReviewOpen(false);
        setActiveRecord(null);
        setActiveRecordType(null);
        if (activeRecordType === 'expense') {
          await expensesQuery.refetch();
        } else {
          await timeOffQuery.refetch();
        }
        return;
      }

      showApprovalToast('error', String(error?.message || error || 'Unable to reject request'));
    }
  };

  const saveVacationApprovalNotificationPreference = async (nextValue) => {
    if (!auth.employee?.id) return;

    const nextSetting = Boolean(nextValue);
    const previousSetting = vacationApprovalNotificationSubscribed;

    setNotificationToggleError(null);
    setVacationApprovalNotificationSubscribed(nextSetting);

    try {
      await updateEmployeeMutation.mutateAsync({
        id: auth.employee.id,
        preferences: {
          ...currentPreferences,
          [VACATION_APPROVAL_NOTIFICATIONS_PREF_KEY]: nextSetting,
        }
      });
    } catch (error) {
      setVacationApprovalNotificationSubscribed(previousSetting);
      setNotificationToggleError(String(error?.message || error));
    }
  };

  if (auth.isLoading || auth.isResolving) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (!canViewPage) {
    return (
      <Message type="error" header="Access denied" showIcon>
        You do not have permission to view approvals.
      </Message>
    );
  }

  if ((canApproveTimeOff && timeOffQuery.isLoading) || (canApproveExpenses && expensesQuery.isLoading)) {
    return <Placeholder.Paragraph rows={6} active />;
  }

  if (timeOffQuery.error || expensesQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(expensesQuery.error?.message || timeOffQuery.error?.message || expensesQuery.error || timeOffQuery.error)}
      </Message>
    );
  }

  return (
    <div className="approvals-page bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Approvals', to: '/approvals' },
          { label: 'Tables' },
        ]}
        icon="bi-check2-square"
        title="Approvals"
        actions={canApproveTimeOff ? (
          <div className="approvals-page__summary">
            <span className="approvals-page__pill approvals-page__pill--pending">Pending: {statusSummary.pending}</span>
            <span className="approvals-page__pill">Approved: {statusSummary.approved}</span>
            <span className="approvals-page__pill">Rejected: {statusSummary.rejected}</span>
          </div>
        ) : null}
      />

      <FilterToolbar>
        <FilterField label="Employees" basis={240} max={340}>
          <CheckPicker
            data={employeeOptions}
            countable={false}
            renderValue={renderEmployeeSelection(employeeOptions)}
            value={employeeFilter}
            onChange={(nextValue) => setEmployeeFilter(nextValue || [])}
            placeholder="All Employees"
            searchable
            preventOverflow
            renderExtraFooter={renderSelectAllFooter(employeeOptions, employeeFilter, setEmployeeFilter)}
            block
          />
        </FilterField>

        <FilterField label="Statuses" basis={180} max={260}>
          <CheckPicker
            data={statusOptions}
            value={statusFilter}
            onChange={(nextValue) => setStatusFilter(nextValue || [])}
            placeholder="All Statuses"
            countable
            searchable={false}
            preventOverflow
            renderExtraFooter={renderSelectAllFooter(statusOptions, statusFilter, setStatusFilter)}
            block
          />
        </FilterField>

        <FilterField label="Date Range" basis={230} max={320}>
          <DateRangePicker
            value={dateRange}
            onChange={(nextValue) => setDateRange(nextValue || null)}
            format="MM/dd/yyyy"
            character=" – "
            placeholder="All Dates"
            isoWeek={false}
            weekStart={0}
            ranges={quickDateRanges}
            preventOverflow
            block
          />
        </FilterField>

        {isSelfAdmin ? (
          <FilterField basis={280} max={340}>
            <div className="approvals-page__email-toggle d-flex align-items-center justify-content-center gap-2">
              <span className="text-muted small">Receive emails for approvals</span>
              <Toggle
                checked={vacationApprovalNotificationSubscribed}
                checkedChildren="On"
                unCheckedChildren="Off"
                disabled={updateEmployeeMutation.isPending}
                onChange={(next) => saveVacationApprovalNotificationPreference(Boolean(next))}
              />
            </div>
          </FilterField>
        ) : null}
      </FilterToolbar>

      {notificationToggleError ? (
        <Message type="error" showIcon>
          {notificationToggleError}
        </Message>
      ) : null}

      {canApproveTimeOff ? (
        <div className="approvals-page__table-card">
          <div className="approvals-page__section-title">Time Off Approvals</div>
          <TimeOffList
            requests={filteredTimeOff}
            onView={openViewTimeOff}
            onReview={openReviewTimeOff}
            onCancel={() => {}}
            showEmployeeInfo
            showEmployeeEmail={false}
            showRequestType={false}
            showCreatedOn
            createdOnColumnWidth={220}
            actionsColumnWidth={140}
            combineDateColumns
            combinedDatesColumnWidth={220}
            notesColumnMinWidth={140}
            notesColumnFlexGrow={1}
            startEndDisplay="datetime"
            showCancelAction={false}
            tableClassName="approvals-page__table"
          />
          <div className="approvals-page__table-footer">
            Showing {filteredTimeOff.length} request{filteredTimeOff.length === 1 ? '' : 's'}
          </div>
        </div>
      ) : null}

      {canApproveExpenses ? (
        <div className="approvals-page__table-card">
          <div className="approvals-page__section-title approvals-page__section-title--with-action">
            <span>Expense Approvals</span>
            {canManagePayroll ? (
              <button
                type="button"
                className="bdc-linkbtn"
                onClick={() => navigate('/approvals/payroll-view')}
              >
                See payroll view
              </button>
            ) : null}
          </div>
          <ExpenseList
            expenses={expenseRows}
            onView={openViewExpense}
            onOpenGroup={(group) => setOpenGroupId(group.id)}
            onReview={openReviewExpense}
            onCancel={() => {}}
            showEmployeeInfo
            showPaidOn={false}
            showCancelAction={false}
            actionsColumnWidth={170}
          />
          <div className="approvals-page__table-footer">
            {/* Every expense is a row now, grouped or not, so the count is the
                whole filtered list. Groups are named separately because they
                account for rows the total doesn't explain on its own. */}
            Showing {filteredExpenses.length} request{filteredExpenses.length === 1 ? '' : 's'}
            {expenseView.groups.length > 0
              ? ` in ${expenseView.groups.length} group${expenseView.groups.length === 1 ? '' : 's'} and ${expenseView.ungrouped.length} on their own`
              : ''}
          </div>
        </div>
      ) : null}

      <ExpenseGroupModal
        open={Boolean(openGroup)}
        onClose={() => setOpenGroupId(null)}
        group={openGroup}
        // Opening an expense from the group hands off to the same review modal
        // the table uses, so approving inside a group is the normal flow.
        onOpenExpense={(expense) => {
          setOpenGroupId(null);
          // Same split the table makes: pending rows open for a decision,
          // already-decided ones open read-only.
          if (isPendingStatus(expense?.status)) {
            openReviewExpense(expense);
          } else {
            openViewExpense(expense);
          }
        }}
        showApprovalDetails
      />

      <ApprovalReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        record={activeRecord}
        recordType={activeRecordType}
        approvalBlockedReason={activeRecordType === 'expense'
          && currentEmployeeId
          && String(activeRecord?.employee_id || '') === currentEmployeeId
          ? 'You cannot approve your own request'
          : null}
        onApprove={submitApprove}
        onReject={submitReject}
        onSaveComment={submitComment}
        onMarkPaid={canManagePayroll ? openMarkPaid : undefined}
        loading={
          approveExpenseMutation.isPending ||
          approveTimeOffMutation.isPending ||
          rejectExpenseMutation.isPending ||
          rejectTimeOffMutation.isPending
        }
        commentLoading={commentExpenseMutation.isPending || commentTimeOffMutation.isPending}
        markPaidLoading={markExpensePaidMutation.isPending}
      />

      <ExpenseMarkPaidModal
        open={Boolean(paidExpense)}
        value={paidOnDate}
        onChange={setPaidOnDate}
        onSubmit={submitMarkPaid}
        onClose={closeMarkPaid}
        loading={markExpensePaidMutation.isPending}
      />
    </div>
  );
}
